import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { fmtUSD } from '../../utils/formatters';
import { adminApi } from '../../services/api';

const AllTrades = () => {
  const dispatch = useDispatch();
  const { allTrades } = useSelector((state) => state.admin);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [editTradeId, setEditTradeId] = useState(null);
  const [editEntryPrice, setEditEntryPrice] = useState('');
  const [editExitPrice, setEditExitPrice] = useState('');
  const [editPnl, setEditPnl] = useState('');
  const [editSwap, setEditSwap] = useState('');
  const [editTime, setEditTime] = useState('');

  const filteredTrades = (allTrades || []).filter(trade => {
    const search = searchTerm.toLowerCase();
    return ((trade.instrumentId || '').toLowerCase().includes(search) || (trade.userEmail || '').toLowerCase().includes(search) || (trade.firstName || '').toLowerCase().includes(search)) && (filter === 'all' || trade.direction === filter);
  });

  const handleSaveAll = async (tradeId) => {
    try {
      const payload = {};
      if (editEntryPrice) payload.entryPrice = Number(editEntryPrice);
      if (editExitPrice) payload.exitPrice = Number(editExitPrice);
      if (editPnl) payload.pnl = Number(editPnl);
      if (editSwap) payload.swap = Number(editSwap);
      if (editTime) payload.time = editTime;
      await adminApi.put(`/admin/trades/${tradeId}`, payload);
      dispatch(showToast({ message: 'Trade updated', type: 'success' }));
      setEditTradeId(null); setEditEntryPrice(''); setEditExitPrice(''); setEditPnl(''); setEditSwap(''); setEditTime('');
      window.location.reload();
    } catch (e) { dispatch(showToast({ message: 'Failed', type: 'error' })); }
  };

  const getDirectionBadge = (dir) => dir === 'buy'
    ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]"><i className="fas fa-arrow-trend-up text-[8px]" /> BUY</span>
    : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b91c1c]"><i className="fas fa-arrow-trend-down text-[8px]" /> SELL</span>;

  const totalPnl = filteredTrades.reduce((s, t) => s + (Number(t.pnl) || 0), 0);
  const totalSwap = filteredTrades.reduce((s, t) => s + (Number(t.swap) || 0), 0);
  const winRate = filteredTrades.length > 0 ? (filteredTrades.filter(t => (Number(t.pnl) || 0) > 0).length / filteredTrades.length * 100) : 0;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">ALL TRADES</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">View and manage all platform trades</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8] text-xs" />
            <input 
              type="text" 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              placeholder="Search..." 
              className="pl-10 pr-4 py-2.5 bg-white border-2 border-[#cbd5e1] rounded-xl text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all w-full sm:w-56 placeholder:text-[#94a3b8]" 
            />
          </div>
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)} 
            className="px-4 py-2.5 bg-white border-2 border-[#cbd5e1] rounded-xl text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all cursor-pointer">
            <option value="all">ALL</option>
            <option value="buy">BUY</option>
            <option value="sell">SELL</option>
          </select>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b-2 border-[#e2e8f0] bg-white">
        {[
          { label: 'TOTAL P&L', value: (totalPnl >= 0 ? '+' : '') + fmtUSD(totalPnl), color: totalPnl >= 0 ? '#059669' : '#dc2626', icon: 'fa-chart-line' },
          { label: 'TOTAL SWAP', value: '-' + fmtUSD(totalSwap), color: '#d97706', icon: 'fa-rotate' },
          { label: 'NET P&L', value: ((totalPnl - totalSwap) >= 0 ? '+' : '') + fmtUSD(totalPnl - totalSwap), color: (totalPnl - totalSwap) >= 0 ? '#059669' : '#dc2626', icon: 'fa-calculator' },
          { label: 'WIN RATE', value: winRate.toFixed(1) + '%', color: '#2563eb', icon: 'fa-trophy' },
        ].map(stat => (
          <div key={stat.label} className="flex items-center gap-3 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${stat.color}15` }}>
              <i className={`fas ${stat.icon} text-sm`} style={{ color: stat.color }} />
            </div>
            <div>
              <div className="text-[8px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider">{stat.label}</div>
              <div className="text-lg font-extrabold mt-0.5" style={{ color: stat.color }}>{stat.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0]">
              {['DATE', 'USER', 'SYMBOL', 'SIDE', 'LOTS', 'ENTRY', 'EXIT', 'P&L', 'SWAP', 'NET', 'STATUS', 'EDIT'].map(h => (
                <th key={h} className={`px-5 py-3.5 text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider ${['LOTS','ENTRY','EXIT','P&L','SWAP','NET'].includes(h) ? 'text-right' : h === 'EDIT' ? 'text-center' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredTrades.length === 0 ? (
              <tr>
                <td colSpan="12" className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-chart-line text-[#94a3b8] text-2xl" />
                  </div>
                  <p className="text-[#475569] font-syne font-extrabold text-sm uppercase tracking-wider">No trades found</p>
                </td>
              </tr>
            ) : filteredTrades.map((trade, idx) => {
              const pnl = Number(trade.pnl) || 0; const swap = Number(trade.swap) || 0; const net = pnl - swap;
              return (
                <tr key={trade.id || idx} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all">
                  {/* Date */}
                  <td className="px-5 py-3 text-[11px] text-[#475569] font-mono font-bold">
                    {editTradeId === trade.id ? (
                      <input type="datetime-local" value={editTime} onChange={e => setEditTime(e.target.value)} 
                        className="w-40 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1 text-[11px] text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]" />
                    ) : (trade.time ? new Date(trade.time).toLocaleString() : '—')}
                  </td>
                  {/* User */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-sm">
                        <span className="text-[9px] font-extrabold text-white">{(trade.firstName || trade.userEmail || 'U')[0]?.toUpperCase()}</span>
                      </div>
                      <div className="text-[12px] text-[#0f172a] font-bold truncate max-w-[120px]">
                        {trade.firstName ? `${trade.firstName} ${trade.lastName || ''}` : trade.userEmail}
                      </div>
                    </div>
                  </td>
                  {/* Symbol */}
                  <td className="px-5 py-3 font-syne text-xs font-extrabold text-[#0f172a]">{trade.instrumentId}</td>
                  {/* Side */}
                  <td className="px-5 py-3">{getDirectionBadge(trade.direction)}</td>
                  {/* Lots */}
                  <td className="px-5 py-3 text-right font-mono text-[12px] font-extrabold text-[#0f172a]">{Number(trade.qty || 0).toFixed(2)}</td>
                  {/* Entry */}
                  <td className="px-5 py-3 text-right font-mono text-[12px] font-extrabold text-[#0f172a]">
                    {editTradeId === trade.id ? (
                      <input type="number" value={editEntryPrice} onChange={e => setEditEntryPrice(e.target.value)} 
                        className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f] text-right" />
                    ) : Number(trade.entryPrice || 0).toFixed(2)}
                  </td>
                  {/* Exit */}
                  <td className="px-5 py-3 text-right font-mono text-[12px] font-extrabold text-[#0f172a]">
                    {editTradeId === trade.id ? (
                      <input type="number" value={editExitPrice} onChange={e => setEditExitPrice(e.target.value)} 
                        className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f] text-right" />
                    ) : Number(trade.exitPrice || 0).toFixed(2)}
                  </td>
                  {/* P&L */}
                  <td className={`px-5 py-3 text-right font-mono text-[12px] font-extrabold ${pnl >= 0 ? 'text-[#059669]' : 'text-[#dc2626]'}`}>
                    {editTradeId === trade.id ? (
                      <input type="number" value={editPnl} onChange={e => setEditPnl(e.target.value)} 
                        className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f] text-right" />
                    ) : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`}
                  </td>
                  {/* Swap */}
                  <td className="px-5 py-3 text-right font-mono text-[12px] font-extrabold text-[#d97706]">
                    {editTradeId === trade.id ? (
                      <input type="number" value={editSwap} onChange={e => setEditSwap(e.target.value)} 
                        className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f] text-right" />
                    ) : (swap > 0 ? '-$' + swap.toFixed(2) : '—')}
                  </td>
                  {/* Net */}
                  <td className={`px-5 py-3 text-right font-mono text-[12px] font-extrabold ${net >= 0 ? 'text-[#059669]' : 'text-[#dc2626]'}`}>
                    {net >= 0 ? '+' : ''}{net.toFixed(2)}
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]">
                      <i className="fas fa-check-circle text-[8px]" /> {trade.status || 'FILLED'}
                    </span>
                  </td>
                  {/* Edit */}
                  <td className="px-5 py-3 text-center">
                    {editTradeId === trade.id ? (
                      <div className="flex gap-2 justify-center">
                        <button onClick={() => handleSaveAll(trade.id)} 
                          className="w-8 h-8 rounded-lg bg-[#059669] text-white text-sm font-extrabold hover:bg-[#047857] transition-all flex items-center justify-center">✓</button>
                        <button onClick={() => setEditTradeId(null)} 
                          className="w-8 h-8 rounded-lg bg-[#dc2626] text-white text-sm font-extrabold hover:bg-[#b91c1c] transition-all flex items-center justify-center">✕</button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => { setEditTradeId(trade.id); setEditEntryPrice(String(trade.entryPrice || 0)); setEditExitPrice(String(trade.exitPrice || 0)); setEditPnl(String(pnl)); setEditSwap(String(swap)); setEditTime(trade.time ? new Date(trade.time).toISOString().slice(0,16) : ''); }} 
                        className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">
                        <i className="fas fa-pen mr-1" /> EDIT
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllTrades;